// #region imports

// --- Angular
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  BreakpointObserver,
  BreakpointState
} from '@angular/cdk/layout';

// --- RxJs
import { BehaviorSubject, Subscription, debounceTime, distinctUntilChanged } from 'rxjs';

// --- 3rd Party
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';

// --- HS Code
import { Constants, RegistrationCodeValidationResultModel, RegistrationCodeValidationResults, RegistrationResultModel } from '../../../models';
import { ResponseBaseService, SelfRegistrationBaseService } from '../../../core/services';

// #endregion

@Component({
  selector: 'hs-self-registration-shell',
  templateUrl: './self-registration-shell.component.html',
  styleUrls: ['./self-registration-shell.component.scss']
})
export class SelfRegistrationShellComponent implements OnInit, OnDestroy {

  private _registrationValidationSubscription: Subscription = Subscription.EMPTY;
  private _registrationResultSubscription: Subscription = Subscription.EMPTY;

  public isValid$: BehaviorSubject<boolean | null> = new BehaviorSubject<boolean | null>(null);
  public errorMessage$: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);

  public errorRedirectMessage: string | null = null;
  public participantId: string | null = null;

  private _breakpointObserverSubscription?: Subscription;

  constructor(
    public activeModal: NgbActiveModal,
    private _router: Router,
    private _selfRegistrationService: SelfRegistrationBaseService,
    private _responseService: ResponseBaseService,
    private _translate: TranslateService,
    private _breakpointObserver: BreakpointObserver,
  ) {

    this._selfRegistrationService.clearRegistration();
    this._registrationValidationSubscription = this._selfRegistrationService.validationResult$.subscribe((validationResultModel: RegistrationCodeValidationResultModel) => {
      if ((validationResultModel.registrationCode as string) === undefined) return;
      const registrationCode: string = validationResultModel.registrationCode as string;

      if (validationResultModel.validationResult > RegistrationCodeValidationResults.unvalidated) {
        const isValid: boolean = (validationResultModel.validationResult === RegistrationCodeValidationResults.success);
        this.isValid$.next(isValid);
      }

      switch (validationResultModel.validationResult) {
        case RegistrationCodeValidationResults.success:
          this.errorMessage$.next(null);
          this._selfRegistrationService.register(registrationCode);
          break;
        case RegistrationCodeValidationResults.campaignNotFound:
          this.errorMessage$.next(this._translate.instant('surveySelfRegistrationErrorNoSurvey'));
          break;
        case RegistrationCodeValidationResults.registrationCodeExpired:
          this.errorMessage$.next(this._translate.instant('surveySelfRegistrationErrorCodeExpired'));
          break;
        case RegistrationCodeValidationResults.campaignNotLive:
          this.errorMessage$.next(this._translate.instant('surveySelfRegistrationErrorInvalidState'));
          break;
        case RegistrationCodeValidationResults.campaignNotSelfReg:
          this.errorMessage$.next(this._translate.instant('surveySelfRegistrationErrorInvalidSurvey'));
          break;
        case RegistrationCodeValidationResults.alreadyPaticipating:
          const participantId: string = validationResultModel.participantId as string;
          if (!participantId) {
            this.errorMessage$.next(this._translate.instant('surveySelfRegistrationErrorAlreadyParticipating'));
          } else {
            this.participantId = participantId;
            this.errorRedirectMessage = this._translate.instant('surveySelfRegistrationErrorAlreadyParticipatingLink');
          }
          break;
      }
    });

    this._registrationResultSubscription = this._selfRegistrationService.registrationResult$.subscribe((registrationResult: RegistrationResultModel) => {
      switch (registrationResult.validationResult) {
        case RegistrationCodeValidationResults.success:
          this.isValid$.next(null);
          this._selfRegistrationService.clearRegistration();
          this._responseService.clear();
          const modalData = {
            action: 'survey',
            id: registrationResult.surveyReference || registrationResult.participantId
          };
          this.activeModal.close(modalData);
          break;
        case RegistrationCodeValidationResults.campaignNotFound:
          this.errorMessage$.next(this._translate.instant('surveySelfRegistrationErrorNoSurvey'));
          break;
        case RegistrationCodeValidationResults.registrationCodeExpired:
          this.errorMessage$.next(this._translate.instant('surveySelfRegistrationErrorCodeExpired'));
          break;
        case RegistrationCodeValidationResults.campaignNotLive:
          this.errorMessage$.next(this._translate.instant('surveySelfRegistrationErrorInvalidState'));
          break;
        case RegistrationCodeValidationResults.campaignNotSelfReg:
          this.errorMessage$.next(this._translate.instant('surveySelfRegistrationErrorInvalidSurvey'));
          break;
        case RegistrationCodeValidationResults.alreadyPaticipating:
          const participantId: string = registrationResult.participantId as string;
          if (!participantId) {
            this.errorMessage$.next(this._translate.instant('surveySelfRegistrationErrorAlreadyParticipating'));
          } else {
            this.participantId = participantId;
            this.errorRedirectMessage = this._translate.instant('surveySelfRegistrationErrorAlreadyParticipatingLink');
          }
          break;
        }
    });
  }

  ngOnInit(): void {
    this._breakpointObserverSubscription = this._breakpointObserver.observe([
      '(max-width: 960px)'
    ])
    .pipe(
      distinctUntilChanged(),
    )
    .subscribe((state: BreakpointState) => {
      this.activeModal.update({fullscreen: state.matches});
    });
  }

  ngOnDestroy(): void {
    if (this._registrationValidationSubscription && (this._registrationValidationSubscription !== Subscription.EMPTY)) {
      this._registrationValidationSubscription.unsubscribe();
      this._registrationValidationSubscription = Subscription.EMPTY;
    }
    if (this._registrationResultSubscription && (this._registrationResultSubscription !== Subscription.EMPTY)) {
      this._registrationResultSubscription.unsubscribe();
      this._registrationResultSubscription = Subscription.EMPTY;
    }

    if (this._breakpointObserverSubscription && (this._breakpointObserverSubscription !== Subscription.EMPTY)) {
      this._breakpointObserverSubscription.unsubscribe();
      this._breakpointObserverSubscription = Subscription.EMPTY;
    }
  }

  onRedirectClick(participantId: string): void {
    if (participantId.length === 0) {
      this.activeModal.dismiss();
    }

    const modalData = {
      action: 'dashboard',
      id: participantId
    }
    this.activeModal.close(modalData);
  }

  onRegistrationCodeEntered(registrationCode: string): void {
    this.isValid$.next(null);
    this.errorMessage$.next(null);
    if (registrationCode.trim().length === 6) {
      this._selfRegistrationService.validateRegistrationCode(registrationCode);
    }
  }
}
